import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  Typography,
  Switch,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TablePagination,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';

import { absenceTypes, approvalStatuses } from './data/data';

import { useTableState } from '../../../hooks/useTableState';
import {
  corregirTexto,
  firstLetterUpperCase,
  hasPemissions,
  usarFuncionesComposicion,
} from '../../../commons/utils';
// import { SinDatos } from '../../commons/generics/SinDatos';
import { IconByString } from '../../../components/IconByString';
import { GenericTableHead, HeadCell } from '../../../commons/generics/GenericTableHead';
import { StyledTableCell, StyledTableRow } from '../../../commons/generics/StyledTableComponents';
import { StatusChip } from '../../../commons/generics/StatusChip';
import { ModalConfirmDeactivation } from '../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../components/modals/ModalConfirmRestore';
import {
  getEmployeeAbsencesStore,
  deleteEmployeeAbsenceStore,
  restoreEmployeeAbsenceStore,
} from '../../../store/employee_absences/thunks';
import { SinDatos } from '../../../commons/generics/SinDatos';
import { paddingCardsTables } from 'commons';

const absenceTypeLabels: Record<string, string> = {
  VACATION: 'Vacaciones',
  SICK_LEAVE: 'Incapacidad',
  UNPAID_LEAVE: 'Permiso No Remunerado',
  MATERNITY: 'Licencia de Maternidad',
};

const headCells: HeadCell[] = [
  { id: 'index', label: '#', sortable: false },
  { id: 'employee', label: 'Empleado', sortable: true },
  { id: 'absence_type', label: 'Tipo Ausencia', sortable: true },
  { id: 'dates', label: 'Fechas', sortable: false },
  { id: 'approval_status', label: 'Aprobación', sortable: false, align: 'center' },
  { id: 'status', label: 'Estado', sortable: true, align: 'center' },
  { id: 'actions', label: 'Acciones', sortable: false, align: 'center' },
];

interface Props {
  onEdit: (absence: any) => void;
  onView: (absence: any) => void;
  refreshDataTrigger?: number; // to trigger reload from parent
}

export const TableAusenciaEmpleado = ({ onEdit, onView, refreshDataTrigger }: Props) => {
  const dispatch = useDispatch<any>();

  const [searchTermLocal, setSearchTermLocal] = useState('');
  const [hasPermission, setHasPermission] = useState(true);

  const tableState = useTableState('employeeAbsences');
  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    checked,
    handleSwitchChange,
    openElem,
    setOpenElem,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    handleResponse,
    ResponseComponents,
    getSortedAndFilteredList,
    order,
    orderBy,
    handleRequestSort,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    searchTerm,
    setSearchTerm,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    isServerSide,
    updatePaginationFromList,
  } = tableState;

  const { checkingPreload: loading } = useSelector((state: any) => state.loading);
  const list = useSelector((state: any) => state.employeeAbsences.employeeAbsences_list);

  useEffect(() => {
    updatePaginationFromList(list);
  }, [list, updatePaginationFromList]);

  const checkPermissions = async () => {
    const view = await hasPemissions('modulo.thumano', 'modulo.empleado.ausencias', 'view');
    if (!view) {
      setHasPermission(false);
    } else {
      setCanView(true);
    }

    const edit = await hasPemissions('modulo.thumano', 'modulo.empleado.ausencias', 'edit');
    if (edit) setCanEdit(true);

    const del = await hasPemissions('modulo.thumano', 'modulo.empleado.ausencias', 'delete');
    if (del) setCanDelete(true);

    const restore = await hasPemissions('modulo.thumano', 'modulo.empleado.ausencias', 'restore');
    if (restore) setCanRestore(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const reloadData = (statusVal = checked ? 0 : 1) => {
    dispatch(getEmployeeAbsencesStore(pagePagination, rowsPerPage, searchTermLocal, statusVal));
  };

  useEffect(() => {
    if (canView) {
      reloadData();
    }
  }, [dispatch, canView, refreshDataTrigger]);

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setSearchTermLocal(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) => getEmployeeAbsencesStore(1, rowsPerPage, p, checked ? 0 : 1),
      () => reloadData(checked ? 0 : 1),
      null,
      'buscar ausencias'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;

    if (searchTermLocal) {
      handleSearchHook(
        searchTermLocal,
        dispatch,
        (p: string) => getEmployeeAbsencesStore(1, rowsPerPage, p, status),
        () => reloadData(status),
        null,
        'buscar ausencias'
      );
    } else {
      dispatch(getEmployeeAbsencesStore(1, rowsPerPage, '', status));
    }
  };

  const onResetSearch = () => {
    setSearchTermLocal('');
    handleResetSearchHook(() => reloadData());
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(
      getEmployeeAbsencesStore(value, rowsPerPage, searchTermLocal, checked ? 0 : 1)
    );
    await handleResponse(response, 'listar ausencias', false);
  };

  const handleDelete = (absence: any) => {
    setOpenElem(absence);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (absence: any) => {
    setOpenElem(absence);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      deleteEmployeeAbsenceStore(
        openElem.id,
        searchTermLocal,
        pagePagination,
        rowsPerPage,
        searchTermLocal,
        checked ? 0 : 1
      )
    );
    await handleResponse(response, 'desactivar ausencia', true);
    if (response?.ok) setOpenElem(null);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      restoreEmployeeAbsenceStore(
        openElem.id,
        searchTermLocal,
        pagePagination,
        rowsPerPage,
        searchTermLocal,
        checked ? 0 : 1
      )
    );
    await handleResponse(response, 'restaurar ausencia', true);
    if (response?.ok) setOpenElem(null);
  };

  const filteredList = getSortedAndFilteredList(
    dataTableList || [],
    ['absenceType', 'approvalStatus'], // Campos a filtrar/ordenar
    'status',
    checked ? 0 : 1
  );

  if (!hasPermission) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No tienes permisos para ver este contenido.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: paddingCardsTables }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar por empleado..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTermLocal}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTermLocal && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card
          sx={{
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table>
              <GenericTableHead
                headCells={headCells}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filteredList.length === 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={7} align="center">
                      <SinDatos />
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  filteredList
                    .slice(
                      isServerSide ? 0 : page * rowsPerPage,
                      isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                    )
                    .map((absence: any, index: number) => (
                      <StyledTableRow key={absence.id} hover>
                        <StyledTableCell>
                          <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {page * rowsPerPage + index + 1}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconByString
                              iconName="PersonOutline"
                              sx={{ color: 'primary.main', fontSize: 20 }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                              {usarFuncionesComposicion(
                                corregirTexto,
                                firstLetterUpperCase,
                                absence?.empleado
                                  ? `${absence.empleado.name} ${absence.empleado.last_name}`
                                  : 'Empleado Desconocido'
                              )}
                            </Typography>
                          </Box>
                        </StyledTableCell>

                        <StyledTableCell>
                          <Typography variant="body2" color="textSecondary">
                            {usarFuncionesComposicion(
                              corregirTexto,
                              firstLetterUpperCase,
                              absenceTypeLabels[absence?.absenceType] || absence?.absenceType
                            )}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell>
                          <Typography variant="body2" color="textSecondary">
                            {absence?.startDate} a {absence?.endDate}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          <Typography
                            variant="body2"
                            color={
                              absence?.approvalStatus === 'APPROVED'
                                ? 'success.main'
                                : 'textSecondary'
                            }
                          >
                            {approvalStatuses.find((s) => s.value === absence?.approvalStatus)
                              ?.label || absence?.approvalStatus}
                          </Typography>
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          <StatusChip status={absence.status} />
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canView && onView(absence)}
                                disabled={!canView}
                                color="info"
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            <span
                              style={{
                                cursor:
                                  !canEdit || absence.status !== 1 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => canEdit && absence.status === 1 && onEdit(absence)}
                                disabled={!canEdit || absence.status !== 1}
                                color="primary"
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            {absence.status === 1 ? (
                              <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canDelete && handleDelete(absence)}
                                  disabled={!canDelete}
                                  color="error"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            ) : (
                              <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => canRestore && handleRestore(absence)}
                                  disabled={!canRestore}
                                  color="success"
                                >
                                  <RestoreOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            )}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                )}
              </TableBody>
            </Table>
            {filteredList.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredList.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            )}
            {filteredList.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 1,
                  padding: 2.5,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Pagination
                  sx={{ justifyContent: 'center' }}
                  color="primary"
                  count={itemCountPagination}
                  page={pagePagination}
                  onChange={changePaginationPage}
                  showFirstButton
                  showLastButton
                  shape="circular"
                />
              </Box>
            )}
          </TableContainer>
        </Card>
      )}

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
        title="Anular Ausencia"
        description="¿Está seguro de anular/desactivar este registro de ausencia?"
      />

      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
        title="Restaurar Ausencia"
        description="¿Está seguro de restaurar este registro de ausencia?"
      />
    </Box>
  );
};
